import type { Slide, TextProps } from '@features/powerpoint';
import { themeV2 } from '../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../../../utils/slide-header-v2';
import { formatters } from '@utils/formattersV2';
import { keyTakeawaysSparkle } from '../../../../../images';
import {
  NEWLINE_REGEX,
  parsedTakeawaysV2,
} from '../../../../../domain/key-takeaways-v2';

const NUMBER_VALUE_FONT_SIZE = 30;
const VALUE_SUBTEXT_FONT_SIZE = 10;

const fieldHeaderOptions: TextProps['options'] = {
  color: themeV2.colors.inmarketGreen,
  fontFace: DEFAULT_FONT,
  fontSize: 10,
  bold: true,
  breakLine: true,
};

const sectionTitleOptions: TextProps['options'] = {
  fontFace: DEFAULT_FONT,
  fontSize: 18,
  bold: true,
};

type OverallCampaignResultsV2Props = {
  slideMaster: string;
  totalVisits: number;
  incrementalVisits: number;
  incrementalLift: number;
  costPerVisit: number;
  costPerIncrementalVisit: number;
  confidenceLevel: number;
  ftaOverallResults: string;
};

export const overallCampaignResultsSlideV2 = (
  props: OverallCampaignResultsV2Props
): Slide => {
  const keyTakeaways = props.ftaOverallResults
    .split(NEWLINE_REGEX)
    .filter(line => line.trim().length > 0);

  const hasKeyTakeaways = keyTakeaways.length > 0;

  return {
    slideMaster: props.slideMaster,
    images: hasKeyTakeaways
      ? [
          {
            path: keyTakeawaysSparkle,
            x: 0.33,
            y: 4.58,
            h: 0.4,
            w: 0.4,
          },
        ]
      : [],
    shapes: hasKeyTakeaways
      ? [
          {
            shapeName: 'line' as const,
            options: {
              h: 0,
              w: 9.47,
              x: 3.19,
              y: 4.8,
              line: {
                color: themeV2.colors.inmarketGreen,
                width: 1,
              },
            },
          },
        ]
      : [],
    texts: [
      contentSlideHeaderV2('Overall Campaign Results'),
      {
        text: 'Visit Summary',
        options: {
          ...sectionTitleOptions,
          color: themeV2.colors.metricBlue,
          x: 0.33,
          y: 1.1,
          h: 0.5,
          w: 7.3,
          valign: 'top',
        },
      },
      {
        text: [
          {
            text: 'Total Visits'.toUpperCase(),
            options: {
              ...fieldHeaderOptions,
            },
          },
          {
            text: formatters.visits(props.totalVisits, { full: true }),
            options: {
              fontSize: NUMBER_VALUE_FONT_SIZE,
              color: themeV2.colors.metricBlue,
              fontFace: DEFAULT_FONT,
              bold: true,
              breakLine: true,
            },
          },
          {
            text: 'Projected Visits After Ad Exposure',
            options: {
              fontSize: VALUE_SUBTEXT_FONT_SIZE,
              color: themeV2.colors.metricBlue,
              fontFace: DEFAULT_FONT,
            },
          },
        ],
        options: {
          valign: 'top',
          lineSpacingMultiple: 1.2,
          h: 1.46,
          w: 3.02,
          x: 0.33,
          y: 1.68,
        },
      },
      {
        text: [
          {
            text: 'Cost Per Visit'.toUpperCase(),
            options: {
              ...fieldHeaderOptions,
              breakLine: true,
            },
          },
          {
            text: formatters.currency(props.costPerVisit, {
              full: true,
              decimals: 2,
            }),
            options: {
              fontSize: NUMBER_VALUE_FONT_SIZE,
              color: themeV2.colors.metricBlue,
              fontFace: DEFAULT_FONT,
              bold: true,
              breakLine: true,
            },
          },
          {
            text: 'Spend / Total Visits',
            options: {
              fontSize: VALUE_SUBTEXT_FONT_SIZE,
              color: themeV2.colors.metricBlue,
              fontFace: DEFAULT_FONT,
            },
          },
        ],
        options: {
          lineSpacingMultiple: 1.2,
          valign: 'top',
          h: 1.06,
          w: 1.94,
          x: 0.33,
          y: 2.99,
        },
      },
      ...attributionSummarySection(props),
      ...(hasKeyTakeaways
        ? [
            {
              text: 'Key Takeaways',
              options: {
                ...sectionTitleOptions,
                color: themeV2.colors.black,
                x: 0.83,
                y: 4.55,
                w: 4.52,
                h: 0.47,
              },
            },
            {
              text: parsedTakeawaysV2(keyTakeaways),
              options: {
                h: 2.2,
                w: 11.98,
                x: 0.33,
                y: 5.05,
                valign: 'top' as const,
                paraSpaceAfter: 5,
                fontSize: 12,
                fontFace: DEFAULT_FONT,
              },
            },
          ]
        : []),
    ],
  };
};

const attributionSummarySection = (
  props: Pick<
    OverallCampaignResultsV2Props,
    | 'incrementalVisits'
    | 'incrementalLift'
    | 'costPerIncrementalVisit'
    | 'confidenceLevel'
  >
): NonNullable<Slide['texts']> => [
  {
    text: 'Attribution Summary',
    options: {
      ...sectionTitleOptions,
      color: themeV2.colors.black,
      x: 6.26,
      y: 1.1,
      h: 0.47,
      w: 4.52,
    },
  },
  {
    text: [
      {
        text: 'Incremental Visits'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.visits(props.incrementalVisits, { full: true }),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
          bold: true,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.06,
      w: 1.94,
      x: 6.26,
      y: 1.68,
    },
  },
  {
    text: [
      {
        text: 'Incremental Lift'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.lift(props.incrementalLift),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
          bold: true,
          breakLine: true,
        },
      },
      {
        text: `Confidence Level: ${formatters.confidenceLevel(
          props.confidenceLevel,
          props.incrementalLift
        )}`,
        options: {
          breakLine: true,
          fontSize: VALUE_SUBTEXT_FONT_SIZE,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.06,
      w: 1.94,
      x: 9.74,
      y: 1.68,
    },
  },
  {
    text: [
      {
        text: 'Cost Per Incremental Visit'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.currency(props.costPerIncrementalVisit, {
          decimals: 2,
        }),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
          bold: true,
          breakLine: true,
        },
      },
      {
        text: 'Spend / Incremental Visits',
        options: {
          breakLine: true,
          fontSize: VALUE_SUBTEXT_FONT_SIZE,
          color: themeV2.colors.metricBlue,
          fontFace: DEFAULT_FONT,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.06,
      w: 2.66,
      x: 6.26,
      y: 2.99,
    },
  },
];
